import type { RefObject } from 'react'
import { useCallback, useEffect, useRef } from 'react'

import { get, put } from '~/api/fetch-client'
import type { PlayQueue } from '~/api/types'
import { toPersistedQueue } from '~/components/player/persisted-queue'
import type { PlayerState, QueueItem } from '~/components/player/types'
import { trackToQueueItem } from '~/components/player/utils'

interface UseQueuePersistenceOptions {
  queue: QueueItem[]
  currentIndex: number
  isPlaying: boolean
  stateRef: RefObject<PlayerState>
  restoredRef: RefObject<boolean>
  skipNextPlayRef: RefObject<boolean>
  pendingSeekRef: RefObject<number | null>
  currentTimeRef: RefObject<number>
  setState: (updater: PlayerState | ((prev: PlayerState) => PlayerState)) => void
}

export function useQueuePersistence({
  queue,
  currentIndex,
  isPlaying,
  stateRef,
  restoredRef,
  skipNextPlayRef,
  pendingSeekRef,
  currentTimeRef,
  setState,
}: UseQueuePersistenceOptions): void {
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const saveNow = useCallback(() => {
    if (!restoredRef.current) return
    const body = toPersistedQueue(stateRef.current, currentTimeRef.current)
    if (!body) return
    put('/play-queue', body).catch(() => {})
  }, [stateRef, restoredRef, currentTimeRef])

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null
      saveNow()
    }, 2000)
  }, [saveNow])

  // Restore queue from server on mount. Deliberately mount-only: a re-run would
  // overwrite the live queue with the server's copy mid-playback.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only by design, see above
  useEffect(() => {
    get<PlayQueue>('/play-queue')
      .then((data) => {
        if (data.tracks.length === 0) {
          restoredRef.current = true
          return
        }
        const restored = data.tracks.map((track) => ({
          ...trackToQueueItem(track),
          radioGenerated: track.radioGenerated,
        }))
        const idx = data.current ? restored.findIndex((item) => item.id === data.current) : -1
        skipNextPlayRef.current = true
        if (data.position && data.position > 0) {
          pendingSeekRef.current = data.position
        }
        setState((s) => ({
          ...s,
          queue: restored,
          currentIndex: idx >= 0 ? idx : 0,
        }))
        restoredRef.current = true
      })
      .catch(() => {
        restoredRef.current = true
      })
  }, [])

  // Debounced save on queue/index change. `restoredRef` is a guard against saving
  // before the initial restore lands, not an input that should retrigger a save.
  // biome-ignore lint/correctness/useExhaustiveDependencies: restoredRef is a guard, not an input
  useEffect(() => {
    if (!restoredRef.current) return
    scheduleSave()
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
      }
    }
  }, [queue, currentIndex, scheduleSave])

  // Periodically save position during playback (every 30s).
  // biome-ignore lint/correctness/useExhaustiveDependencies: restoredRef is a guard, not an input
  useEffect(() => {
    if (!isPlaying || !restoredRef.current) return
    const interval = setInterval(saveNow, 30_000)
    return () => clearInterval(interval)
  }, [isPlaying, saveNow])

  // Save on tab close — flush any pending debounce
  useEffect(() => {
    const onBeforeUnload = () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
      }
      if (!restoredRef.current) return
      const body = toPersistedQueue(stateRef.current, currentTimeRef.current)
      if (!body) return
      // keepalive lets the request survive page teardown
      fetch('/api/v1/play-queue', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        keepalive: true,
        body: JSON.stringify(body),
      }).catch(() => {})
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [stateRef, restoredRef, currentTimeRef])
}
